import React, { Fragment } from 'react';
import {
    faCheckCircle,
    faSave,
    faWarehouse,
} from '@fortawesome/free-solid-svg-icons';
import {
    Button,
    Input,
    FormGroup,
    Col,
    Row,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEqual } from 'lodash-es';
import { Prompt, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as moment from 'moment';
import AddressInput from '../address/AddressInput';
import {
    AppPageForm,
    FlexCenterRow,
    FlexColumnCenter,
    FormCheckbox,
    FormDivider,
    onFieldChange,
    onReactSelectChanged,
    SimpleTable,
    SubHeading,
    ToastMessage,
    YesNoBadge,
    FormLabel,
} from '../common/forms/FormElements';
import { BaseFormViewModel } from '../common/ViewModel';
import { Can } from '../Can';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import {
    OrganizationTypes,
    Organization,
    OrganizationContact,
} from './Organization';
import OrganizationContactForm from './OrganizationContactForm';
import { util } from '../Util';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';

class HaulingLocationForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.contactFormRef = React.createRef();

        const stateBase = {
            contactsFormValidated: false,
            customers: [],
            isCustomer: false,
            isOperationsCenter: false,
            isSubcontractor: false,
            isParentCustomer: false,
            isProspect: false,
            organization: new Organization(),
            types: [],
            selectedContact: null,
            showContactsForm: false,
            parentCompanies: [],
            subcontractorCustomersOptions: [],
            contactFormIsReadOnly: false,
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.updateOrganization = this.updateOrganization.bind(this);
    }

    // #region INIT
    componentDidMount() {
        this.populateState();
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps
            && this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }

        if (
            prevProps.location.hash == '#billingRates'
            && this.billingRatesRef.current != undefined
        ) {
            this.billingRatesRef.current.scrollIntoView();
        }
    }

    onAddContact = () => {
        this.context.setFormOpened(true);
        this.contactFormRef.current.resetForm();
        this.setState({
            selectedContact: /* createFakeContact() */ new OrganizationContact(),
            showContactsForm: true,
            allowContactTypeEdit: true,
        });
    };


    onChange = onFieldChange;

    // #endregion

    onClearErrors = () => this.setState((state) => ({ errors: {} }));

    onClose = (response) => {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    };

    onDelete = async (e) => {
        const { organization } = this.state;
        const response = await util.fetch
            .delete(ApiRoutes.company.delete(organization.id))
            .catch(this.onCatchFetchError);
        if (response) this.onClose(response);
    };

    onEditContact = (contact) => {
        this.contactFormRef.current.resetForm();

        // Only allow contact type edit if it's a new contact that's being edited before being saved.
        // Can't edit contact types for an already saved contact, in other words.
        const allowCTEdit = !contact.id;

        this.setState({
            selectedContact: contact,
            showContactsForm: true,
            allowContactTypeEdit: allowCTEdit,
        });
    };

    onJobEndChanged = (ev) => {
        const { organization } = this.state;

        const val = ev.target.value;
        const timeValue = ev.target.value
            ? moment(val, ['h:mm A']).format('HH:mm')
            : '';

        organization.billingOTJobEnd = timeValue;
        this.setState({ organization });
    };

    onJobStartChanged = (ev) => {
        const { organization } = this.state;

        const val = ev.target.value;
        const timeValue = ev.target.value
            ? moment(val, ['h:mm A']).format('HH:mm')
            : '';

        organization.billingOTJobStart = timeValue;
        this.setState({ organization });
    };


    onOTDaysChanged = (event, otDay) => {
        const { organization } = { ...this.state };
        organization.billingOTDays ^= otDay;
        this.setState({ organization: { ...organization } });
    };

    onOrgTypeChanged = async () => {
        const { organization } = { ...this.state };

        const isBilling = (organization.types ?? []).includes(
            OrganizationTypes.Billing,
        );
        const isPayroll = (organization.types ?? []).includes(
            OrganizationTypes.Payroll,
        );
        const isDispatch = (organization.types ?? []).includes(
            OrganizationTypes.Dispatch,
        );
        const isCustomer = (organization.types ?? []).includes(
            OrganizationTypes.Customer,
        );
        const isOperationsCenter = (organization.types ?? []).includes(
            OrganizationTypes.OperationsCenter,
        );
        const isSubcontractor = (organization.types ?? []).includes(
            OrganizationTypes.CustomerSubcontractor,
        );
        const isParentCustomer = (organization.types ?? []).includes(
            OrganizationTypes.ParentCustomer,
        );
        const isProspect = (organization.types ?? []).includes(
            OrganizationTypes.Prospect,
        );
        let customers = [];
        let parentRequired = false;
        let parentCustomers = [];
        let parentCompanies = [];
        let parentCompanyLabelText = '';

        if (isBilling) {
            // do nothing.  this is top tier hierarchy
        } else if (isPayroll && !isBilling) {
            // if not a billing type, the billing company must be provided.
            parentCompanies = await util.fetch.js(
                ApiRoutes.typeAheads.billingCompanies(),
            );
            parentCompanyLabelText = 'Billing Company';
            parentRequired = true;
        } else if (isDispatch && !isPayroll && !isBilling) {
            // If not a payroll type and a dispatch location, need the payroll company.
            parentCompanies = await util.fetch.js(
                ApiRoutes.typeAheads.payrollCompanies(),
            );
            parentCompanyLabelText = 'Payroll Company';
            parentRequired = true;
        }

        let useInvoicingRequired = false;
        if (isOperationsCenter) {
            customers = await util.fetch.js(ApiRoutes.typeAheads.customers());
            // Remove any self-reference as a protection.
            customers = (customers ?? []).filter((x) => x.value !== organization.id)
                ?? [];

            if (organization.customerCompanyId !== null) {
                const opCenterParent = await util.fetch.js(
                    ApiRoutes.company.byId(organization.customerCompanyId),
                );
                useInvoicingRequired = opCenterParent
                    && opCenterParent.operationsCenterUseInvoicing;
            }
        }

        if (!isParentCustomer) {
            parentCustomers = await util.fetch.js(
                ApiRoutes.typeAheads.parentCustomers(),
            );
        }

        // Remove self-reference as a protection.
        parentCompanies = (parentCompanies ?? []).filter(
            (x) => x.value !== organization.id,
        ) ?? [];

        if (isBilling) organization.parentCompanyId = null;

        // If this org is not a contractor, clear out customers array.
        if (!isSubcontractor) organization.subcontractorCustomers = [];

        this.setState({
            customers: [...customers],
            isOperationsCenter,
            isCustomer,
            isSubcontractor,
            isParentCustomer,
            isProspect,
            parentCompanyLabelText,
            parentCompanies: [...parentCompanies],
            parentCustomers: [...parentCustomers],
            organization: { ...organization },
            parentRequired,
            useInvoicingRequired,
        });
    };

    onOrgTypeSelectChanged = (item) => {
        const { organization } = { ...this.state };
        organization.types = (item ?? []).map((x) => x.value) ?? [];
        this.setState({ organization });
        this.onOrgTypeChanged();
    };

    onOrganizationContactSaved = async () => {
        await this.setState({ contactFormIsReadOnly: true });

        const { selectedContact, organization } = this.state;
        organization.contacts = util.array.insertIfNotExists(
            organization.contacts,
            selectedContact,
        );
        this.contactFormRef.current.resetForm();
        this.setState({
            organization,
            showContactsForm: false,
            contactFormIsReadOnly: false,
        });
    };

    onPhoneChanged = (event) => {
        const { value } = event.target;
        const { organization } = { ...this.state };
        organization.phone = value;
        this.setState({ organization: { ...organization } });
    };


    onSelectChanged = onReactSelectChanged;

   
    // #region METHODS
    onSubmit = async (e) => {
    // Clear any fluent api errors
        this.onClearErrors();
        this.setSaving(true);

        const { organization } = { ...this.state };

        /** Revert temporary hashes used in row keys.  Need this to get around
            fluent API.
        */
        for (const contact of organization.contacts) contact.id = contact.id?.constructor === String ? null : contact.id;

        //for (const rate of organization.billingRates) {
        //    rate.billingRateId = rate.billingRateId.constructor === String
        //        ? null
        //        : rate.billingRateId;

        //    rate.billingRate.id = rate.billingRate.id.constructor === String
        //        ? null
        //        : rate.billingRate.id;
        //}

        const clonedGroups = util.object.clone([
            ...organization.companyChargeTypeGroups,
        ]);
        clonedGroups.map((x) => ({
            companyId: organization.id,
            chargeTypeGroupId: x.id,
            invoiceDescription: x.invoiceDescrption,
            billRate: x.billRate,
            chargeTypeGroup: x.chargeTypeGroup,
        }));
        organization.chargeTypeGruops = { ...clonedGroups };

        this.props.location.pathname === AppNavPaths.HaulingLocationNew
            ? this.createOrganization(organization)
            : this.updateOrganization(organization);
    };

    onTimeEntryEndChanged = (ev) => {
        const { organization } = this.state;

        const val = ev.target.value;
        const timeValue = ev.target.value
            ? moment(val, ['h:mm A']).format('HH:mm')
            : '';

        organization.billingOTShiftEnd = timeValue;
        this.setState({ organization });
    };

    onTimeEntryStartChanged = (ev) => {
        const { organization } = this.state;

        const val = ev.target.value;
        const timeValue = ev.target.value
            ? moment(val, ['h:mm A']).format('HH:mm')
            : '';

        organization.billingOTShiftStart = timeValue;
        this.setState({ organization });
    };

    getDate = (dateInput) => {
        const formats = ['YYYY-MM-DD HH:mm A', 'L-d-YYYY HH:mm A', 'M/D/YYYY HH:mm A'];
        let startDate = '';

        formats.forEach(format => {
            const date = moment(dateInput, format);

            if (date.isValid()) {
                startDate = date.format('HH:mm A');
            }
        });

        return startDate;
    };

    setSaving = (b) => this.setState({ saving: b });

    canEditCodes = () => {
        if (!this.context || !this.context.checkUserHasPermission) {
            return false;
        }

        return this.context.checkUserHasPermission('company_code.edit');
    };

    createOrganization = async (organization) => {
        const response = await util.fetch
            .post(ApiRoutes.company.create(), organization)
            .catch(this.handleSaveError);

        if (response && !isNaN(response)) {
            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message="Saved."
                />,
            );
            util.navigation.localRedirect(
                this,
                `${AppNavPaths.HaulingLocation}/${response}`,
            );
        }

        this.setSaving(false);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    async populateState() {
        const orgId = this.props.match.params.id;

        const [
            companyTypes,
            contracts,
            companyStatuses,
            organization,
            allCustomers,
            chargeTypes
        ] = await Promise.all([
            util.fetch.js(ApiRoutes.typeAheads.companyTypes()),
            util.fetch.js(ApiRoutes.typeAheads.contracts()),
            util.fetch.js(ApiRoutes.typeAheads.companyStatuses()),
            orgId
                ? util.fetch.js(ApiRoutes.company.byId(orgId))
                : new Organization(),
            util.fetch.js(ApiRoutes.typeAheads.subcontractingCustomers()),
            util.fetch.js(ApiRoutes.typeAheads.chargeTypesBillable()),
        ]);

        var selectedContractId = 0;
        if (orgId) {
            const haulingLocation = await util.fetch.js(ApiRoutes.contract.haulingLocation(orgId));
            selectedContractId = haulingLocation;
        }

        const [usStates] = await Promise.all([
            util.fetch.js(ApiRoutes.typeAheads.USStates()),
        ]);

        organization.billingOTShiftStart = organization.billingOTShiftStart
            ? this.getDate(organization.billingOTShiftStart)
            : '';
        organization.billingOTShiftEnd = organization.billingOTShiftEnd
            ? this.getDate(organization.billingOTShiftEnd)
            : '';

        organization.billingOTJobStart = organization.billingOTJobStart
            ? this.getDate(organization.billingOTJobStart)
            : '';
        organization.billingOTJobEnd = organization.billingOTJobEnd
            ? this.getDate(organization.billingOTJobEnd)
            : '';

        organization.types = [11];

        const originalData = util.object.clone(organization);

        this.setState(
            {
                originalData,
                organization,
                loading: false,
                companyTypes,
                contracts,
                companyStatuses,
                saving: false,
                subcontractorCustomersOptions: allCustomers,
                chargeTypes,
                allowContactTypeEdit: false,
                usStates,
                useInvoicingRequired: false,
                selectedContractId: selectedContractId
            },
            async function () {
                await this.onOrgTypeChanged();
            },
        );
    }

    resetForm = () => this.setState({ formValidated: false });

    subcontractorCustomersChanged = (selection) => {
        const value = selection ? [...selection] : [];
        const customers = value.map((x) => x.value) ?? [];
        this.setState(
            (state) => (
                (state.organization.subcontractorCustomers = customers), state
            ),
        );
    };

    toggleCTGroupModal = async () => {
        const { showCTGroupModal } = this.state;

        this.setState({
            showCTGroupModal: !showCTGroupModal,
        });
    };

    updateOrganization = async (organization) => {

        const { selectedContractId } = this.state;
        
        const response = await util.fetch
            .put(ApiRoutes.company.byId(organization.id), organization)
            .catch(this.handleSaveError);

        await util.fetch.put(ApiRoutes.contract.addUpdateHaulingLocation(organization.id, selectedContractId ?? 0));

        response.billingOTShiftStart = organization.billingOTShiftStart
            ? this.getDate(response.billingOTShiftStart)
            : '';

        response.billingOTShiftEnd = organization.billingOTShiftEnd
            ? this.getDate(response.billingOTShiftEnd)
            : '';

        response.billingOTJobStart = organization.billingOTJobStart
            ? this.getDate(response.billingOTJobStart)
            : '';
        response.billingOTJobEnd = organization.billingOTJobEnd
            ? this.getDate(response.billingOTJobEnd)
            : '';

        if (response && parseInt(response.id ?? 0) >= 0) {
            this.setState({
                organization: response,
                originalData: util.object.clone(response),
                saving: false,
                formValidated: false,
            });

            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message={`[${response.name}] saved.`}
                />,
            );
        }

        this.setSaving(false);
    };

    renderContacts = () => {
        const { contacts } = this.state.organization;
        if (!contacts) {
            return '';
        }

        return contacts.map((contact, i) => {
            let primaryPhone = '';

            if (contact.officePhoneIsPrimary) primaryPhone = contact.officePhone ?? contact.mobilePhone;
            else primaryPhone = contact.mobilePhone ?? contact.officePhone;

            return (
                <tr key={i}>
                    <td>{`${contact.lastName}, ${contact.firstName}`}</td>
                    <td>{contact.title}</td>
                    <td>
                        <a href={`mailto: ${contact.email}`}>{contact.email}</a>
                    </td>
                    <td>{primaryPhone}</td>
                    <td>{contact.locationContactTypeName}</td>
                    <td>{contact.isActive ? 'Yes' : 'No'}</td>
                    <Can I="edit" a="company">
                        <CommonContext.Consumer>
                            {(value) => (
                                <td>
                                    <Button
                                        disabled={
                                            !!value.formIsOpen
                                                || this.state.organization
                                                    .companyStatusId != 1
                                        }
                                        type="button"
                                        color="outline-primary"
                                        onClick={() => this.onEditContact(contact)}
                                        size="sm"
                                    >
                    Edit
                                    </Button>
                                </td>
                            )}
                        </CommonContext.Consumer>
                    </Can>
                </tr>
            );
        });
    };

    renderJobRows = () => {
        const { jobs } = this.state.organization;
        if (!jobs) return '';
        return jobs.map((job, i) => (
            <tr key={i}>
                <td>{job.id}</td>
                <td>{job.name}</td>
                <td>
                    <YesNoBadge value={job.active} />
                </td>
            </tr>
        ));
    };

    render() {
        const {
            customers,
            subcontractorCustomersOptions,
            isCustomer,
            isOperationsCenter,
            isSubcontractor,
            isParentCustomer,
            isProspect,
            parentCustomers,
            parentCompanies,
            parentRequired,
            parentCompanyLabelText,
            organization,
            originalData,
            errors,
            formValidated,
            companyTypes,
            saving,
            showContactsForm,
            selectedContact,
            contactFormIsReadOnly,
            allowContactTypeEdit,
            usStates,
            useInvoicingRequired,
            companyStatuses,
            contracts
        } = this.state;

        if (!organization) return '';

        const existing = Boolean(organization.id);
        const selectedSubcontractorCustomers = [
            ...(subcontractorCustomersOptions ?? []).filter((x) => (organization?.subcontractorCustomers ?? []).includes(
                x.value,
            )),
        ] ?? '';
        const dispatchSelected = (organization.types ?? []).includes(
            OrganizationTypes.Dispatch,
        );

        const payrollSelected = (organization.types ?? []).includes(
            OrganizationTypes.Payroll,
        );

        const isBilling = (organization.types ?? []).includes(
            OrganizationTypes.Billing,
        );

        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};

        const isInvoicingAllowed = !!tenantSettings.invoicingEnabled;
        const contractExpirationEmailEnabled = Boolean(
            tenantSettings.contractExpiryDistributionList,
        );

        return (
            <>
                <Prompt
                    when={!saving && !isEqual(originalData, organization)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    formShown={this.context.formIsOpen}
                    formId="organizationForm"
                    formHeadingIcon={faWarehouse}
                    formHeading={
                        !organization.id
                            ? 'New Hauling Location'
                            : 'Edit Hauling Location'
                    }
                    formName="organizationForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    onClearErrors={this.onClearErrors}
                    loading={this.state.loading}
                    onBack={() => this.props.history.push(AppNavPaths.HaulingLocations)}
                    backLabel="Back to Hauling Locations"
                >
                    <Row>
                        <Col
                            xl={{ size: 6, offset: 3 }}
                            lg={{ size: 8, offset: 2 }}
                            md={{ size: 10, offset: 1 }}
                            sm={{ size: 12, offset: 0 }}
                        >
                            <SubHeading>Details</SubHeading>

                            <FormGroup>
                                <FormLabel
                                    htmlFor="companyStatus"
                                    text="Status"
                                />
                                <ValidatedSelect
                                    id="companyStatusId"
                                    name="organization.companyStatusId"
                                    options={companyStatuses}
                                    value={
                                        companyStatuses?.find(
                                            (s) => s.value
                                                === organization.companyStatusId,
                                        ) ?? ''
                                    }
                                    onChange={this.onSelectChanged}
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel
                                    htmlFor="selectedContractId"
                                    text="job"
                                />
                                <ValidatedSelect
                                    id="selectedContractId"
                                    name="selectedContractId"
                                    options={contracts}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.description}
                                    value={ contracts?.find((s) => s.id == this.state.selectedContractId) ?? '' }
                                    onChange={(selection) => {
                                        var selectedContractId = selection?.id ?? null;
                                        this.setState({ selectedContractId: selectedContractId });
                                    }}
                                />
                            </FormGroup>

                            <FormDivider />

                            <FormGroup>
                                <FormLabel
                                    htmlFor="organizationName"
                                    text="Name"
                                    required
                                />
                                <Input
                                    id="organizationName"
                                    name="organization.name"
                                    value={organization.name ?? ''}
                                    onChange={this.onChange}
                                    placeholder="Enter Name (max 150 characters)"
                                    maxLength="150"
                                    pattern="[^()\/><\][\\\x22;|]+"
                                    type="text"
                                    required
                                />
                                <small className="invalid-feedback text-danger">
                  Name is required and can only contain the
                  following special characters: hyphens and
                  periods.
                                </small>
                            </FormGroup>
                            <AddressInput
                                className="org-address-input"
                                namePrefix="organization"
                                onChange={this.onChange}
                                address={organization}
                                onStateChanged={this.onSelectChanged}
                                single
                            />
                            {(!!dispatchSelected || isBilling) && (
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="phone"
                                        text="Phone"
                                        required
                                    />
                                    <input
                                        id="phone"
                                        placeholder="ex: 555-555-5555"
                                        name="organization.phone"
                                        pattern={
                                            util.validation.patterns.htmlPhone
                                        }
                                        autoComplete="off"
                                        type="tel"
                                        className="form-control"
                                        onChange={this.onPhoneChanged}
                                        defaultValue={organization.phone}
                                        required
                                    />
                                    <small className="invalid-feedback text-danger">
                  A valid phone number is required. (555-555-5555)
                                    </small>
                                </FormGroup>
                            )}

                            {!!(tenantSettings ?? {}).enableAlternateBillTo && (
                                <>
                                    <FormCheckbox
                                        small
                                        id="useAsBillingAddress"
                                        checked={
                                            organization.useAsBillingAddress
                                        }
                                        onChange={(e) => {
                                            const { organization } = this.state;
                                            organization.useAsBillingAddress = e.target.checked;
                                            this.setState({
                                                organization,
                                            });
                                        }}
                                        labelText="Also Use For Billing Address"
                                    />

                                    {!organization.useAsBillingAddress && (
                                        <>
                                            <FormGroup>
                                                <SubHeading className="mt-4">
                      Billing Address
                                                </SubHeading>
                                            </FormGroup>

                                            <FormGroup>
                                                <FormLabel
                                                    htmlFor="billingName"
                                                    text="Name"
                                                />
                                                <input
                                                    id="billingName"
                                                    name="organization.billingName"
                                                    autoComplete="off"
                                                    type="text"
                                                    className="form-control"
                                                    defaultValue={
                                                        organization.billingName
                                                    }
                                                    onChange={this.onChange}
                                                />
                                            </FormGroup>

                                            <FormGroup>
                                                <FormLabel
                                                    htmlFor="billingAddress1"
                                                    text="Address Line 1"
                                                />
                                                <input
                                                    id="billingAddress1"
                                                    name="organization.billingAddress1"
                                                    autoComplete="off"
                                                    type="text"
                                                    className="form-control"
                                                    defaultValue={
                                                        organization.billingAddress1
                                                    }
                                                    onChange={this.onChange}
                                                />
                                            </FormGroup>

                                            <FormGroup>
                                                <FormLabel
                                                    htmlFor="billingAddress2"
                                                    text="Address Line 2"
                                                />
                                                <input
                                                    id="billingAddress2"
                                                    name="organization.billingAddress2"
                                                    autoComplete="off"
                                                    type="text"
                                                    className="form-control"
                                                    defaultValue={
                                                        organization.billingAddress2
                                                    }
                                                    onChange={this.onChange}
                                                />
                                            </FormGroup>

                                            <FormGroup>
                                                <FormLabel
                                                    htmlFor="billingCity"
                                                    text="City"
                                                />
                                                <input
                                                    id="billingCity"
                                                    name="organization.billingCity"
                                                    autoComplete="off"
                                                    type="text"
                                                    className="form-control"
                                                    defaultValue={
                                                        organization.billingCity
                                                    }
                                                    onChange={this.onChange}
                                                />
                                            </FormGroup>

                                            <FormGroup>
                                                <FormLabel
                                                    htmlFor="billingState"
                                                    text="State"
                                                />
                                                <ValidatedSelect
                                                    id="billingState"
                                                    name="organization.billingState"
                                                    options={usStates}
                                                    value={
                                                        usStates?.find(
                                                            (s) => s.value
                                                                === organization.billingState,
                                                        ) ?? ''
                                                    }
                                                    onChange={
                                                        this.onSelectChanged
                                                    }
                                                />
                                            </FormGroup>

                                            <FormGroup>
                                                <FormLabel
                                                    htmlFor="billingZip"
                                                    text="Zip Code"
                                                />
                                                <input
                                                    id="billingZip"
                                                    name="organization.billingZip"
                                                    autoComplete="off"
                                                    type="text"
                                                    className="form-control"
                                                    defaultValue={
                                                        organization.billingZip
                                                    }
                                                    onChange={this.onChange}
                                                />
                                            </FormGroup>
                                        </>
                                    )}
                                </>
                            )}

                            <FormGroup
                                hidden={
                                    !isCustomer
                                    && !dispatchSelected
                                    && !payrollSelected
                                }
                            >
                                <SubHeading className="mt-4">
                  Configurations
                                </SubHeading>
                            </FormGroup>

                            {/*<FormGroup hidden={!dispatchSelected}>*/}
                            {/*    <FormLabel*/}
                            {/*        htmlFor="externalIdMapping"*/}
                            {/*        text="NetSuite ID"*/}
                            {/*    />*/}
                            {/*    <input*/}
                            {/*        id="externalIdMapping"*/}
                            {/*        name="organization.externalId"*/}
                            {/*        type="text"*/}
                            {/*        className="form-control"*/}
                            {/*        onChange={this.onChange}*/}
                            {/*        readOnly={!this.canEditCodes()}*/}
                            {/*        defaultValue={*/}
                            {/*            organization.externalId*/}
                            {/*        }*/}
                            {/*    />*/}
                            {/*</FormGroup>*/}

                            {/*<FormGroup hidden={!payrollSelected}>*/}
                            {/*    <FormLabel*/}
                            {/*        htmlFor="companyCTRCode"*/}
                            {/*        text="Payroll CTR Code"*/}
                            {/*    />*/}
                            {/*    <input*/}
                            {/*        id="companyCTRCode"*/}
                            {/*        name="organization.companyCTRCode"*/}
                            {/*        type="text"*/}
                            {/*        className="form-control"*/}
                            {/*        onChange={this.onChange}*/}
                            {/*        readOnly={!this.canEditCodes()}*/}
                            {/*        defaultValue={*/}
                            {/*            organization.companyCTRCode*/}
                            {/*        }*/}
                            {/*    />*/}
                            {/*</FormGroup>*/}

                            {/*<FormGroup hidden={!dispatchSelected}>*/}
                            {/*    <FormLabel*/}
                            {/*        htmlFor="locationCTRCode"*/}
                            {/*        text="Batch #"*/}
                            {/*    />*/}
                            {/*    <input*/}
                            {/*        id="locationCTRCode"*/}
                            {/*        name="organization.locationCTRCode"*/}
                            {/*        type="text"*/}
                            {/*        className="form-control"*/}
                            {/*        onChange={this.onChange}*/}
                            {/*        readOnly={!this.canEditCodes()}*/}
                            {/*        defaultValue={*/}
                            {/*            organization.locationCTRCode*/}
                            {/*        }*/}
                            {/*    />*/}
                            {/*</FormGroup>*/}

                            <FormGroup
                                hidden={
                                    !isOperationsCenter
                                    || !!isCustomer
                                    || !!isParentCustomer
                                    || !!isProspect
                                }
                            >
                                <FormLabel
                                    htmlFor="customerCompanyId"
                                    text="Customer"
                                    required={
                                        !!isOperationsCenter && !isCustomer
                                    }
                                />
                                <ValidatedSelect
                                    id="customerCompanyId"
                                    name="organization.customerCompanyId"
                                    required={
                                        !!isOperationsCenter && !isCustomer
                                    }
                                    options={customers}
                                    value={
                                        (customers ?? []).find(
                                            (x) => organization.customerCompanyId
                                                === x.value,
                                        ) ?? ''
                                    }
                                    onChange={this.onSelectChanged}
                                    validationMessage="A customer selection is required when the organization types include [Operations Center]."
                                />
                            </FormGroup>

                            {(isCustomer
                                || isParentCustomer
                                || isOperationsCenter) && (
                                <FormGroup>
                                    {(companyTypes ?? []).filter(
                                        (x) => x.value
                                                == OrganizationTypes.Customer,
                                    ).length > 0
                                            && !isProspect
                                            && !isParentCustomer
                                            && !isOperationsCenter && false && (
                                        <FormCheckbox
                                            small
                                            className="ml-3 mt-2"
                                            id="operationsCentersApply"
                                            checked={
                                                organization.operationsCentersApply
                                            }
                                            onChange={(e) => {
                                                const { organization } = this.state;
                                                organization.operationsCentersApply = e.target.checked;
                                                this.setState({
                                                    organization,
                                                });
                                            }}
                                            labelText="Assign Contracts to Operations Centers"
                                        />
                                    )}
                                    {(companyTypes ?? []).filter(
                                        (x) => x.value
                                                == OrganizationTypes.CustomerSubcontractor,
                                    ).length > 0
                                            && !isProspect
                                            && !isParentCustomer
                                            && !isOperationsCenter && (
                                        <FormCheckbox
                                            small
                                            className="ml-3 mt-2"
                                            id="subcontractorsApply"
                                            checked={
                                                organization.subcontractorsApply
                                            }
                                            onChange={(e) => {
                                                const { organization } = this.state;
                                                organization.subcontractorsApply = e.target.checked;
                                                this.setState({
                                                    organization,
                                                });
                                            }}
                                            labelText="Assign Jobs to Customer Subcontractors"
                                        />
                                    )}
                                    {(parentCustomers ?? []).length > 0
                                            && !isOperationsCenter && (
                                        <FormCheckbox
                                            small
                                            className="ml-3 mt-2"
                                            id="parentCustomerApply"
                                            checked={
                                                organization.parentCustomerApply
                                            }
                                            onChange={(e) => {
                                                const { organization } = this.state;
                                                organization.parentCustomerApply = e.target.checked;

                                                if (
                                                    !organization.parentCustomerApply
                                                ) {
                                                    organization.parentCustomerId = null;
                                                }

                                                this.setState({
                                                    organization,
                                                });
                                            }}
                                            labelText="Assign Parent Customer"
                                        />
                                    )}

                                    {isCustomer
                                            && contractExpirationEmailEnabled && (
                                        <FormCheckbox
                                            small
                                            className="ml-3 mt-2"
                                            id="exemptFromExpiringContractEmailNotifications"
                                            checked={
                                                organization.exemptFromExpiringContractEmailNotifications
                                            }
                                            onChange={(e) => {
                                                const { organization } = this.state;
                                                organization.exemptFromExpiringContractEmailNotifications = e.target.checked;
                                                this.setState({
                                                    organization,
                                                });
                                            }}
                                            labelText="Exempt From Expiring Contract Email Notifications"
                                        />
                                    )}

                                    {isCustomer && contractExpirationEmailEnabled && (
                                        <FormCheckbox
                                            small
                                            className="ml-3 mt-2"
                                            id="chkExpandedCancellationReasonRequired"
                                            checked={
                                                organization.expandedCancellationReasonRequired
                                            }
                                            onChange={(e) => {
                                                const { organization } = this.state;
                                                organization.expandedCancellationReasonRequired = e.target.checked;
                                                this.setState({
                                                    organization,
                                                });
                                            }}
                                            labelText="Expanded Cancellation Reason Required"
                                        />
                                    )}

                                    {!!(tenantSettings ?? {}).billingEnabled
                                            && isInvoicingAllowed
                                            && (!isOperationsCenter
                                                || (isOperationsCenter
                                                    && useInvoicingRequired)) && (
                                        <FormCheckbox
                                            small
                                            className="ml-3 mt-2"
                                            id="useInvoicing"
                                            checked={
                                                organization.useInvoicing
                                            }
                                            onChange={(e) => {
                                                const { organization } = this.state;
                                                organization.useInvoicing = e.target.checked;
                                                this.setState({
                                                    organization,
                                                });
                                            }}
                                            labelText="Use Invoicing"
                                        />
                                    )}

                                    {!!(tenantSettings ?? {}).billingEnabled
                                            && isInvoicingAllowed
                                            && this.state.organization.useInvoicing
                                            && !isOperationsCenter && (
                                        <>
                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="operationsCenterUseInvoicing"
                                                checked={
                                                    organization.operationsCenterUseInvoicing
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.operationsCenterUseInvoicing = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText='Require "Use Invoicing" Option on Operation Centers'
                                            />

                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="invoiceHasPO"
                                                checked={
                                                    organization.invoiceHasPO
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.invoiceHasPO = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Include PO On Invoice"
                                            />

                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="invoiceTimesheetUsesWO"
                                                checked={
                                                    organization.invoiceTimesheetUsesWO
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.invoiceTimesheetUsesWO = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Use TC# as timesheet file name"
                                            />

                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="allowMultipleWONumbers"
                                                checked={
                                                    organization.allowMultipleWONumbers
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.allowMultipleWONumbers = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Allow Multiple Workorders"
                                            />

                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="useProodOf40"
                                                checked={
                                                    organization.useProofOf40
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.useProofOf40 = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Use Proof Of 40"
                                            />

                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="billingUsesOT"
                                                checked={
                                                    organization.billingUsesOT
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.billingUsesOT = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="40 Hours Overtime"
                                            />

                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="billingUseDailyOT"
                                                checked={
                                                    organization.billingUseDailyOT
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.billingUseDailyOT = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Daily Overtime"
                                            />
                                        </>
                                    )}
                                </FormGroup>
                            )}      

                            {isCustomer
                                && organization.parentCustomerApply
                                && parentCustomers.length > 0 && (
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="type"
                                        text="Parent Customer"
                                    />
                                    <ValidatedSelect
                                        id="types"
                                        name="organization.parentCustomerId"
                                        required={false}
                                        options={parentCustomers}
                                        value={
                                            (parentCustomers ?? []).find(
                                                (x) => organization.parentCustomerId
                                                        === x.value,
                                            ) ?? ''
                                        }
                                        onChange={this.onSelectChanged}
                                    />
                                </FormGroup>
                            )}
                            <FormGroup hidden={!parentRequired}>
                                <FormLabel
                                    htmlFor="type"
                                    text={parentCompanyLabelText}
                                    required={parentRequired}
                                />
                                <ValidatedSelect
                                    id="types"
                                    name="organization.parentCompanyId"
                                    required={parentRequired}
                                    options={parentCompanies}
                                    value={
                                        (parentCompanies ?? []).find(
                                            (x) => organization.parentCompanyId
                                                === x.value,
                                        ) ?? ''
                                    }
                                    onChange={this.onSelectChanged}
                                    validationMessage="A billing organization is required."
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    {(!!isCustomer || !!isParentCustomer || !!isProspect) && (
                        <Row>
                            <Col>
                                <SubHeading className="mt-4">
                Contacts
                                </SubHeading>

                                {organization.companyStatusId == 1 && (
                                    <SimpleTable
                                        permission="company"
                                        onAddFunction={this.onAddContact}
                                        addLabelText="Add Contact"
                                        tableHeaderLabels={[
                                            { name: 'Name' },
                                            { name: 'Title' },
                                            { name: 'Email' },
                                            { name: 'Phone', width: '100px' },
                                            { name: 'Type' },
                                            { name: 'Is Active?' },
                                        ]}
                                        editable
                                        entities={organization.contacts}
                                        rowRenderer={this.renderContacts}
                                        noDataText="No contacts found."
                                    />
                                )}

                                {organization.companyStatusId != 1 && (
                                    <SimpleTable
                                        permission="company"
                                        tableHeaderLabels={[
                                            { name: 'Name' },
                                            { name: 'Title' },
                                            { name: 'Email' },
                                            { name: 'Phone', width: '100px' },
                                            { name: 'Type' },
                                            { name: 'Is Active?' },
                                        ]}
                                        editable={false}
                                        entities={organization.contacts}
                                        rowRenderer={this.renderContacts}
                                        noDataText="No contacts found."
                                    />
                                )}
                            </Col>
                        </Row>
                    )}

                    {!!isCustomer && !!existing && (
                        <>
                            <Row>
                                <Col>
                                    <SubHeading className="mt-4">
                  Jobs
                                    </SubHeading>
                                    <small
                                        className="mt-2 mb-2"
                                        hidden={
                                            !((organization ?? {}).jobs ?? [])
                                                .length
                                        }
                                    >
                  Displaying the 5 most recent jobs.
                                    </small>
                                    <SimpleTable
                                        permission="company"
                                        tableHeaderLabels={[
                                            { name: 'Job #' },
                                            { name: 'Job Name' },
                                            { name: 'Active?', width: '80px' },
                                        ]}
                                        editable
                                        entities={this.state.organization.jobs}
                                        rowRenderer={this.renderJobRows}
                                        noDataText="No jobs found."
                                    />
                                </Col>
                            </Row>
                            
                        </>
                    )}

                    {!!isSubcontractor && !!existing && (
                        <FlexColumnCenter className="mb-3">
                            <SubHeading className="mt-4">
              Subcontractor For
                            </SubHeading>
                            <FormGroup className="mt-3 subcontractor-form-group">
                                <FormLabel
                                    htmlFor="subcontractorCustomers"
                                    text="Customer(s)"
                                    required
                                />
                                <ValidatedSelect
                                    id="subcontractorCustomers"
                                    name="organization.subcontractorCustomers"
                                    isMulti
                                    options={subcontractorCustomersOptions}
                                    required
                                    value={selectedSubcontractorCustomers}
                                    onChange={
                                        this.subcontractorCustomersChanged
                                    }
                                    validationMessage="A customer selection is required."
                                />
                            </FormGroup>
                        </FlexColumnCenter>
                    )}

                    <FlexCenterRow className="mb-3">
                        <Button
                            disabled={!!this.state.saving}
                            size="sm"
                            type="submit"
                            color="primary"
                            name="organizationForm"
                        >
                            <FontAwesomeIcon className="mr-2" icon={faSave} />
                            {this.state.saving
                                ? 'Saving...'
                                : !existing
                                    ? 'Save Hauling Location'
                                    : 'Save'}
                        </Button>
                    </FlexCenterRow>

                    <div ref={this.billingRatesRef}>&nbsp;</div>

                </AppPageForm>
                {!!isCustomer || isParentCustomer || !!isProspect ? (
                    <>
                        <OrganizationContactForm
                            ref={this.contactFormRef}
                            show={showContactsForm}
                            contact={selectedContact}
                            readOnly={contactFormIsReadOnly}
                            allowContactTypeEdit={allowContactTypeEdit}
                            allowAllContactTypes
                            onClose={() => {
                                this.setState({ showContactsForm: false });
                            }}
                            onChange={(e) => {
                                const { selectedContact } = this.state;
                                selectedContact[e.target.name] = e.target.value;
                                this.setState({
                                    selectedContact,
                                });
                            }}
                            onCheckedChanged={(e) => {
                                const { selectedContact } = this.state;
                                selectedContact[e.target.name] = e.target.checked;
                                this.setState({
                                    selectedContact,
                                });
                            }}
                            onContactTypeChanged={(items) => {
                                const { selectedContact } = this.state;
                                selectedContact.contactTypes = items?.map(
                                    (x) => x.value,
                                ) || [];
                                selectedContact.locationContactTypeName = items?.map((x) => x.label).join(', ') || '';
                                this.setState({
                                    newContact: { ...selectedContact },
                                });
                            }}
                            onSaveCallback={this.onOrganizationContactSaved}
                        />

                        
                    </>
                ) : (
                    ''
                )}

                
            </>
        );
    }
}

export default withRouter(HaulingLocationForm);
